/**
 * Research Workspace Type Definitions
 *
 * Evidence Board data model for EU banking regulatory research.
 * Implements a Rooted DAG structure where all nodes trace back to a single question.
 *
 * See documentation: 2_project_documentation/frontend/research_ui/workspace/types.md
 */

// =============================================================================
// NODE TYPES
// =============================================================================

/**
 * All supported node types for the Evidence Board.
 *
 * Node Categories:
 * - Dynamic: User-provided or computed content (no finding_id, have inline content)
 * - Evidence: Retrieved from regulatory sources (have finding_id → findings lookup)
 *
 * Dynamic Node Types:
 * - question: Research question being investigated (root node)
 * - conclusion: Synthesized answer/finding
 * - input: User-provided data (e.g., portfolio values)
 * - result: Computed/calculated output (e.g., RWA calculation)
 *
 * Evidence Node Types:
 * Legal Structure:
 * - article: Regulatory article/provision
 * - definition: Legal definition ("'X' means...")
 *
 * Legal Modality (Obligations):
 * - requirement: Mandatory - "shall", "must"
 * - option: Permissive - "may", "can choose to"
 * - prohibition: Forbidden - "shall not", "must not"
 * - condition: Conditional - "where", "if", "provided that"
 * - exemption: Exception - "by way of derogation", "does not apply"
 *
 * Quantitative:
 * - threshold: Limits - percentages, amounts, timeframes
 * - calculation: Formulas, methodologies
 *
 * General:
 * - concept: Explanatory concept, catch-all
 */

// Dynamic nodes: have inline content, no finding_id
export type DynamicNodeType = 'question' | 'conclusion' | 'input' | 'result' | 'reasoning' | 'open_endpoint';

// Evidence nodes: have finding_id → findings dict lookup
export type EvidenceNodeType =
  | 'article'
  | 'definition'
  | 'requirement'
  | 'option'
  | 'prohibition'
  | 'condition'
  | 'exemption'
  | 'threshold'
  | 'calculation'
  | 'concept'
  | 'decision_point';

// Structural nodes: used for grouping/organization
export type StructuralNodeType = 'group';

// Plan nodes: project graph components (#89)
export type PlanNodeType = 'task' | 'component' | 'dependency' | 'milestone';

// All node types
export type NodeType = DynamicNodeType | EvidenceNodeType | StructuralNodeType | PlanNodeType;

// =============================================================================
// DOMAIN TYPES (Match Backend)
// =============================================================================

/**
 * Source metadata for provenance tracking
 */
export interface NodeSourceMetadata {
  source_document: string;      // "CRR", "32015R0061", etc.
  source_reference: string;     // "Art. 10(1)(f)"
  confidence_score: number;     // 0.0 - 1.0
}

/**
 * Logical node in the evidence graph.
 * Represents a single piece of evidence, requirement, or finding.
 *
 * Node Categories (by data):
 * - Dynamic nodes (question, conclusion, input, result): have `content`, no `finding_id`
 * - Evidence nodes (article, requirement, etc.): have `finding_id`, no `content`
 *
 * Node Structure (by hierarchy):
 * - Group nodes: have `children` (expandable sub-graph)
 * - End nodes: have `finding_ids[]` (linked findings to display)
 * - Simple nodes: have neither (just explanation)
 */
export interface LogicalNode {
  node_id: string;
  node_type: NodeType;
  cluster_id?: string | null;
  label: string;                        // Short display title (max 80 chars)
  relevance_note: string;               // Why this finding matters to the research question

  // For EVIDENCE nodes (article, requirement, etc.) - links to findings dict
  finding_id?: string;

  // For DYNAMIC nodes (question, conclusion, input, result) - inline content
  content?: string;

  // For GROUP nodes - nested children (expandable sub-graph)
  children?: LogicalNode[];

  // For GROUP nodes - edges between children for internal sub-graph visualization
  child_edges?: ChildEdge[];

  // For END nodes - linked findings to display when selected
  finding_ids?: string[];

  detail?: string | null;               // Full regulatory text (evidence) or extended content (dynamic)
  reference_ids: string[];              // Links to source references
  source_metadata?: NodeSourceMetadata | null;
  created_at?: string;                  // ISO 8601
  updated_at?: string;                  // ISO 8601

  // Plan graph fields (#89)
  workspace_type?: string;              // "research" | "production" | "institution_context"
  workspace_id?: string;                // Workspace ID for plan nodes
  deliverable?: string;                 // What this workspace produces (1-3 words)

  // Validation status (#89)
  validation_status?: 'approved' | 'rejected' | 'review_needed' | null;
  reviews?: Array<{
    review_id: string;
    session_id: string;
    reviewer_type: 'ai' | 'human';
    reviewer_id: string;
    channel: 'grounding' | 'coherence' | 'completeness' | 'general';
    verdict: 'approved' | 'flagged' | 'rejected';
    rationale: string;
    severity: 'info' | 'warning' | 'error';
    created_at: string;
  }>;
}

/**
 * Edge connecting logical nodes.
 * Simple directional connection - relationship is inferred from node types.
 */
export interface LogicalEdge {
  source_node_id: string;
  target_node_id: string;
  created_at?: string;                  // ISO 8601
}

/**
 * Edge connecting child nodes within a LogicalNode's internal sub-graph.
 * Used for rendering internal graph visualization when a group node is expanded.
 */
export interface ChildEdge {
  source_id: string;                    // node_id of source child
  target_id: string;                    // node_id of target child
  label?: string;                       // Optional edge label (e.g., "if condition met")
}

/**
 * Complete logical chain graph.
 * Must form a Rooted DAG with a single question as root.
 * @deprecated Use ProcessGraph for new implementations
 */
export interface LogicalChainGraph {
  nodes: LogicalNode[];
  edges: LogicalEdge[];
}

// =============================================================================
// UNIFIED PROCESS GRAPH MODEL (Issue #48)
// =============================================================================
// Flat structure with parent_id references for hierarchical visualization.
// Supports both research documentation and workflow execution nodes.
// =============================================================================

/**
 * Unified node types spanning research documentation AND workflow execution.
 */
export type ProcessNodeType =
  // Structural
  | 'group'           // Container for sub-graph (collapsible)
  // Research (regulatory understanding)
  | 'question'        // Research question (root)
  | 'article'         // Regulatory article reference
  | 'requirement'     // Mandatory obligation ("shall")
  | 'option'          // Permissive ("may")
  | 'prohibition'     // Forbidden ("shall not")
  | 'definition'      // Legal definition
  | 'threshold'       // Quantitative limit
  | 'condition'       // Conditional ("if", "where")
  | 'exemption'       // Exception/derogation
  | 'conclusion'      // Research finding
  // Execution (workflow steps)
  | 'input'           // Data input step
  | 'transform'       // Calculation/processing
  | 'decision'        // Conditional branch
  | 'output'          // Result/report
  | 'validation';     // Compliance check

/**
 * Edge types distinguishing relationship semantics.
 */
export type ProcessEdgeType =
  // Research relationships
  | 'implies'         // A logically implies B
  | 'supports'        // A provides evidence for B
  | 'references'      // A cites/references B
  | 'defines'         // A defines the term in B
  // Execution relationships
  | 'sequence'        // A executes, then B executes
  | 'data_flow'       // A's output feeds B's input
  | 'conditional';    // If condition, then B (else C)

/**
 * Unified node for research documentation AND workflow execution.
 * Structure is flat (not nested) with parent_id for hierarchy.
 */
export interface ProcessNode {
  // Identity
  node_id: string;
  cluster_id?: string | null;
  parent_id: string | null;       // Parent node ID (for grouping/hierarchy)

  // Type
  node_type: ProcessNodeType;

  // Display
  label: string;                  // Short title (max 200 chars)
  content?: string;               // Human-readable explanation
  detail?: string;                // Full regulatory text or extended content

  // Source (for research nodes)
  source_document?: string;       // Source document (e.g., "CRR")
  source_reference?: string;      // Specific reference (e.g., "Art. 10(1)(f)")
  finding_ids?: string[];         // Links to evidence findings

  // Execution (for workflow nodes)
  execution_config?: Record<string, unknown>;  // n8n node configuration

  // Plan graph fields
  deliverable?: string;                 // What this workspace produces (1-3 words)

  // Metadata
  created_at?: string;
  updated_at?: string;

  // Validation status (#89)
  validation_status?: 'approved' | 'rejected' | 'review_needed' | null;
}

/**
 * Edge connecting nodes. Works within groups AND across hierarchy levels.
 */
export interface ProcessEdge {
  // Identity
  edge_id: string;

  // Connection
  source_node_id: string;
  target_node_id: string;

  // Type
  edge_type: ProcessEdgeType;

  // Display
  label?: string;                 // Edge label for visualization

  // Execution (for conditional edges)
  condition?: string;             // Condition expression
}

/**
 * The unified graph structure for research and execution workflows.
 * Flat list of nodes with parent_id references.
 */
export interface ProcessGraph {
  // Identity
  graph_id: string;
  name: string;
  description?: string;

  // Structure
  nodes: ProcessNode[];
  edges: ProcessEdge[];

  // Metadata
  version?: string;
  created_at?: string;
  updated_at?: string;
}

// =============================================================================
// UI TYPES (Frontend Only)
// =============================================================================

/**
 * Position on the canvas
 */
export interface NodePosition {
  x: number;
  y: number;
}

/**
 * Viewport state for pan/zoom
 */
export interface ViewportState {
  x: number;
  y: number;
  zoom: number;
}

/**
 * Complete UI metadata for the workspace.
 * Uses snake_case to match backend schema.
 */
export interface WorkspaceUIState {
  node_positions: Record<string, NodePosition>;
  viewport: ViewportState;
  selected_node_id: string | null;
}

// =============================================================================
// COMPOSITE TYPES
// =============================================================================

/**
 * Complete workspace state combining domain and UI data
 */
export interface WorkspaceState {
  graph: LogicalChainGraph;
  ui: WorkspaceUIState;
}

// =============================================================================
// REFERENCE SYSTEM TYPES
// =============================================================================

/**
 * Details returned when resolving a reference marker.
 * Used by SourcePanel to display citation details.
 */
export interface ReferenceDetails {
  marker_id: string;                    // Unique reference marker ID
  label: string;                        // Display label (e.g., "[1]")
  context: string;                      // Why this source was cited
  citation_text: string;                // Brief citation text
  finding_id: string;                   // Linked finding ID
  source_document_name: string;         // Document name (e.g., "CRR")
  source_document_celex: string;        // EUR-Lex CELEX number (e.g., "32013R0575")
  source_reference: string;             // Specific reference (e.g., "Article 26(1)(a)")
  source_reference_hierarchy: string;   // Path in document (e.g., "Part Two > Title I")
  source_text: string;                  // Full text of the source
  source_snippet: string;               // Relevant excerpt (highlighted)
  node_ids: string[];                   // Related node IDs in the evidence graph
}

// =============================================================================
// REACT FLOW ADAPTER TYPES
// =============================================================================

/**
 * Data payload for custom React Flow nodes
 *
 * Node Categories (by data):
 * - Dynamic nodes (question, conclusion, input, result): have `content`, no `findingId`
 * - Evidence nodes (article, requirement, etc.): have `findingId`, no `content`
 *
 * Node Structure (by hierarchy):
 * - Group nodes: have `children` (expandable sub-graph)
 * - End nodes: have `findingIds[]` (linked findings to display)
 */
export interface WorkspaceNodeData {
  nodeType: NodeType;
  label: string;
  relevanceNote: string;                // Why this finding matters to the research question

  // For EVIDENCE nodes - links to findings dict
  findingId?: string;

  // For DYNAMIC nodes - inline content
  content?: string;

  // For GROUP nodes - nested children (expandable sub-graph)
  children?: LogicalNode[];

  // For GROUP nodes - edges between children for internal sub-graph visualization
  childEdges?: ChildEdge[];

  // For END nodes - linked findings to display when selected
  findingIds?: string[];

  detail?: string | null;
  referenceIds: string[];
  sourceMetadata?: NodeSourceMetadata | null;
}

/**
 * Props for custom node components
 */
export interface CustomNodeProps {
  id: string;
  data: WorkspaceNodeData;
  selected: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get display icon for node type
 */
export function getNodeTypeIcon(nodeType: NodeType): string {
  switch (nodeType) {
    // Dynamic nodes
    case 'question': return '?';
    case 'conclusion': return '✓';
    case 'input': return '→';
    case 'result': return '∑';
    case 'reasoning': return '⟹';
    case 'open_endpoint': return '…';
    // Structural
    case 'group': return '▣';
    // Legal structure
    case 'article': return '§';
    case 'definition': return 'D';
    // Legal modality
    case 'requirement': return 'R';
    case 'option': return 'O';
    case 'prohibition': return '✗';
    case 'condition': return '?';
    case 'exemption': return 'E';
    case 'decision_point': return '◇';
    // Quantitative
    case 'threshold': return '#';
    case 'calculation': return '=';
    // General
    case 'concept': return 'C';
    default: return 'N';
  }
}

/**
 * Get display color for node type
 */
export function getNodeTypeColor(nodeType: NodeType): string {
  // Unique-per-type palette restored (reviewed visually via
  // /tmp/bark-node-colors.html on 2026-04-21). Same as the original
  // pre-overhaul assignments, with two brand-alignment tweaks:
  //   - calculation   : #ec4899 (pink)     → #8b5cf6 (violet)
  //   - decision_point: #d946ef (fuchsia)  → #7c3aed (brand purple)
  // Both warm outliers quieted into the cool-temperature family
  // without collapsing semantic distinction between node types.
  switch (nodeType) {
    // Dynamic nodes
    case 'question':     return '#8b5cf6'; // Purple
    case 'conclusion':   return '#f59e0b'; // Amber
    case 'input':        return '#14b8a6'; // Teal
    case 'result':       return '#a855f7'; // Violet
    case 'reasoning':    return '#0ea5e9'; // Sky blue
    case 'open_endpoint':return '#94a3b8'; // Slate
    // Structural
    case 'group':        return '#64748b'; // Slate
    // Legal structure
    case 'article':      return '#6b7280'; // Gray
    case 'definition':   return '#06b6d4'; // Cyan
    // Legal modality
    case 'requirement':  return '#22c55e'; // Green
    case 'option':       return '#3b82f6'; // Blue
    case 'prohibition':  return '#ef4444'; // Red
    case 'condition':    return '#f97316'; // Orange
    case 'exemption':    return '#eab308'; // Yellow
    case 'decision_point': return '#7c3aed'; // Brand purple (was fuchsia #d946ef)
    // Quantitative
    case 'threshold':    return '#6366f1'; // Indigo
    case 'calculation':  return '#8b5cf6'; // Violet (was pink #ec4899)
    // General
    case 'concept':      return '#64748b'; // Slate
    // Plan nodes (#89)
    case 'task':         return '#8b5cf6'; // Purple (matches research question)
    case 'component':    return '#3b82f6'; // Blue (build target)
    case 'dependency':   return '#6b7280'; // Gray (external input)
    case 'milestone':    return '#f59e0b'; // Amber (checkpoint)
    default:             return '#6b7280'; // Gray
  }
}

/**
 * Get human-readable label for node type
 */
export function getNodeTypeLabel(nodeType: NodeType): string {
  switch (nodeType) {
    // Dynamic nodes
    case 'question': return 'Question';
    case 'conclusion': return 'Conclusion';
    case 'input': return 'Input';
    case 'result': return 'Result';
    case 'reasoning': return 'Reasoning';
    case 'open_endpoint': return 'Open Endpoint';
    // Structural
    case 'group': return 'Group';
    // Evidence nodes
    case 'article': return 'Article';
    case 'definition': return 'Definition';
    case 'requirement': return 'Requirement';
    case 'option': return 'Option';
    case 'prohibition': return 'Prohibition';
    case 'condition': return 'Condition';
    case 'exemption': return 'Exemption';
    case 'decision_point': return 'Decision Point';
    case 'threshold': return 'Threshold';
    case 'calculation': return 'Calculation';
    case 'concept': return 'Concept';
    // Plan nodes (#89)
    case 'task': return 'Research';
    case 'component': return 'Build';
    case 'dependency': return 'Input';
    case 'milestone': return 'Milestone';
    default: return 'Node';
  }
}

/**
 * Check if a node type represents a legal modality (obligation type)
 */
export function isLegalModalityType(nodeType: NodeType): boolean {
  return ['requirement', 'option', 'prohibition', 'condition', 'exemption', 'decision_point'].includes(nodeType);
}

/**
 * Check if a node type represents quantitative information
 */
export function isQuantitativeType(nodeType: NodeType): boolean {
  return ['threshold', 'calculation'].includes(nodeType);
}

/**
 * Check if a node type is a dynamic node (has inline content, no finding_id)
 */
export function isDynamicNodeType(nodeType: NodeType): nodeType is DynamicNodeType {
  return ['question', 'conclusion', 'input', 'result', 'reasoning', 'open_endpoint'].includes(nodeType);
}

/**
 * Check if a node type is an evidence node (has finding_id → findings lookup)
 */
export function isEvidenceNodeType(nodeType: NodeType): nodeType is EvidenceNodeType {
  return !isDynamicNodeType(nodeType);
}

/**
 * Check if a node is a group node (has children - expandable sub-graph)
 */
export function isGroupNode(node: LogicalNode): boolean {
  return Array.isArray(node.children) && node.children.length > 0;
}

/**
 * Check if a node is an end node (has finding_ids - linked findings)
 */
export function isEndNode(node: LogicalNode): boolean {
  return Array.isArray(node.finding_ids) && node.finding_ids.length > 0;
}

/**
 * Get all finding IDs from a node and its descendants recursively
 */
export function getAllFindingIds(node: LogicalNode): string[] {
  const findingIds: string[] = [];

  // Add this node's finding_ids
  if (node.finding_ids) {
    findingIds.push(...node.finding_ids);
  }

  // Add legacy finding_id if present
  if (node.finding_id) {
    findingIds.push(node.finding_id);
  }

  // Recursively collect from children
  if (node.children) {
    for (const child of node.children) {
      findingIds.push(...getAllFindingIds(child));
    }
  }

  return Array.from(new Set(findingIds)); // Deduplicate
}

/**
 * Flatten a hierarchical node structure into a flat array
 * Useful for converting hierarchical structure to flat nodes + edges
 */
export function flattenNodes(node: LogicalNode, parentId?: string): { nodes: LogicalNode[]; edges: { source: string; target: string }[] } {
  const nodes: LogicalNode[] = [node];
  const edges: { source: string; target: string }[] = [];

  // Add edge from parent if exists
  if (parentId) {
    edges.push({ source: parentId, target: node.node_id });
  }

  // Process children recursively
  if (node.children) {
    for (const child of node.children) {
      const result = flattenNodes(child, node.node_id);
      nodes.push(...result.nodes);
      edges.push(...result.edges);
    }
  }

  return { nodes, edges };
}

// =============================================================================
// PROCESS GRAPH HELPER FUNCTIONS
// =============================================================================

/**
 * Check if data is in the new flat ProcessGraph format (has parent_id)
 */
export function isProcessGraphFormat(data: { nodes?: unknown[] }): boolean {
  if (!data.nodes || !Array.isArray(data.nodes) || data.nodes.length === 0) {
    return false;
  }
  // Check if any node has parent_id field (new format)
  return data.nodes.some((n: unknown) =>
    typeof n === 'object' && n !== null && 'parent_id' in n
  );
}

/**
 * Check if a ProcessNode is a group node (has children via parent_id lookup)
 */
export function isProcessGroupNode(node: ProcessNode): boolean {
  return node.node_type === 'group';
}

/**
 * Check if a ProcessNode is a research node
 */
export function isResearchNode(node: ProcessNode): boolean {
  const researchTypes: ProcessNodeType[] = [
    'question', 'article', 'requirement', 'option', 'prohibition',
    'definition', 'threshold', 'condition', 'exemption', 'conclusion'
  ];
  return researchTypes.includes(node.node_type);
}

/**
 * Check if a ProcessNode is an execution/workflow node
 */
export function isExecutionNode(node: ProcessNode): boolean {
  const executionTypes: ProcessNodeType[] = [
    'input', 'transform', 'decision', 'output', 'validation'
  ];
  return executionTypes.includes(node.node_type);
}

/**
 * Get root nodes from a ProcessGraph (nodes with no parent)
 */
export function getProcessGraphRoots(graph: ProcessGraph): ProcessNode[] {
  return graph.nodes.filter(n => n.parent_id === null);
}

/**
 * Get children of a node in a ProcessGraph
 */
export function getProcessNodeChildren(graph: ProcessGraph, nodeId: string): ProcessNode[] {
  return graph.nodes.filter(n => n.parent_id === nodeId);
}

/**
 * Get all descendants of a node in a ProcessGraph
 */
export function getProcessNodeDescendants(graph: ProcessGraph, nodeId: string): ProcessNode[] {
  const children = getProcessNodeChildren(graph, nodeId);
  const descendants = [...children];
  for (const child of children) {
    descendants.push(...getProcessNodeDescendants(graph, child.node_id));
  }
  return descendants;
}

/**
 * Get display color for ProcessNodeType
 */
export function getProcessNodeTypeColor(nodeType: ProcessNodeType): string {
  switch (nodeType) {
    // Structural
    case 'group': return '#6b7280';        // Gray
    // Research
    case 'question': return '#8b5cf6';     // Purple
    case 'article': return '#6b7280';      // Gray
    case 'requirement': return '#22c55e';  // Green
    case 'option': return '#3b82f6';       // Blue
    case 'prohibition': return '#ef4444';  // Red
    case 'definition': return '#06b6d4';   // Cyan
    case 'threshold': return '#6366f1';    // Indigo
    case 'condition': return '#f97316';    // Orange
    case 'exemption': return '#eab308';    // Yellow
    case 'conclusion': return '#f59e0b';   // Amber
    // Execution
    case 'input': return '#14b8a6';        // Teal
    case 'transform': return '#ec4899';    // Pink
    case 'decision': return '#a855f7';     // Violet
    case 'output': return '#10b981';       // Emerald
    case 'validation': return '#22c55e';   // Green
    default: return '#6b7280';             // Gray
  }
}

/**
 * Get display color for ProcessEdgeType
 */
export function getProcessEdgeTypeColor(edgeType: ProcessEdgeType): string {
  switch (edgeType) {
    // Research
    case 'implies': return '#6b7280';      // Gray
    case 'supports': return '#22c55e';     // Green
    case 'references': return '#3b82f6';   // Blue
    case 'defines': return '#06b6d4';      // Cyan
    // Execution
    case 'sequence': return '#6b7280';     // Gray
    case 'data_flow': return '#8b5cf6';    // Purple
    case 'conditional': return '#f97316';  // Orange
    default: return '#6b7280';             // Gray
  }
}

/**
 * Convert a ProcessGraph to LogicalChainGraph (for backwards compatibility)
 * Note: This is a lossy conversion - parent_id hierarchy is lost
 */
export function processGraphToLogicalChain(graph: ProcessGraph): LogicalChainGraph {
  const nodes: LogicalNode[] = graph.nodes.map(n => ({
    node_id: n.node_id,
    node_type: n.node_type as NodeType,
    cluster_id: n.cluster_id ?? null,
    label: n.label,
    relevance_note: n.content || '',
    content: n.content,
    detail: n.detail || null,
    finding_ids: n.finding_ids,
    reference_ids: [],
    source_metadata: n.source_document ? {
      source_document: n.source_document,
      source_reference: n.source_reference || '',
      confidence_score: 1.0
    } : null,
  }));

  const edges: LogicalEdge[] = graph.edges.map(e => ({
    source_node_id: e.source_node_id,
    target_node_id: e.target_node_id,
  }));

  return { nodes, edges };
}
